import CopyDocumentIcon from '@streamlinehq/streamlinehq/img/streamline/copy-document.svg';
import { Button, type ButtonProps } from '@upright/ui/common/generic/components/button';
import { Tooltip } from '@upright/ui/common/generic/components/tooltip';
import { Text } from '@upright/ui/common/generic/components/typography';
import copy from 'copy-to-clipboard';
import { PropsWithChildren, useRef, useState } from 'react';

export type Props = ButtonProps & {
  onCopySuccess?: () => void;
  tooltipSuccessMessage?: string;
  showTooltipOnSuccess?: boolean;
  textToCopy: string | number | (() => string | number);
};

export function CopyToClipboardButton({
  textToCopy,
  onCopySuccess,
  children,
  tooltipSuccessMessage,
  showTooltipOnSuccess,
  ...buttonProps
}: PropsWithChildren<Props>) {
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const [isTootipShown, setIsTooltipShown] = useState(false);

  return (
    <Tooltip
      isOpen={isTootipShown}
      theme="light"
      text={<Text color="green-600">{tooltipSuccessMessage || 'Copied!'}</Text>}
    >
      {/*
       * `aria-label` before the spread, so it is a default a caller can override: a page with
       * many copy buttons (the token galleries, a list of ids) otherwise presents a column of
       * controls all named "Copy", which is unusable via voice control or a screen reader's
       * element list.
       */}
      <Button
        aria-label="Copy"
        {...buttonProps}
        startIcon={children ? undefined : CopyDocumentIcon}
        onPress={() => {
          copy(String(typeof textToCopy === 'function' ? textToCopy() : textToCopy));
          onCopySuccess?.();
          if (showTooltipOnSuccess) {
            setIsTooltipShown(true);
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
            timeoutRef.current = setTimeout(() => {
              setIsTooltipShown(false);
            }, 1500);
          }
        }}
      >
        {children}
      </Button>
    </Tooltip>
  );
}
